/**
 * Названия и порядок статусов заказа.
 *
 * Поток статусов ведёт сервер: доска отдаёт свои колонки, а перетаскивание
 * карточки — это `POST /admin/orders/{id}/status {to}`, где сервер сам
 * выбирает действие за целевым статусом (R-40). Поэтому здесь нет таблицы
 * переходов: она была бы второй, расходящейся копией серверной.
 */
import { formatDayTime, formatMoney } from '@dorixona/utils'
import type { BoardCard, Order, OrderEvent, OrderQuery, OrderStatus, ShipMethod, Sla } from './api'

/** Человеческие названия статусов. Ключи — те, что приходят в API. */
export const STATUS_TEXT: Record<OrderStatus, string> = {
  awaiting_payment: 'Ожидает оплаты',
  payment_expired: 'Оплата просрочена',
  new: 'Новый',
  needs_attention: 'Требует решения',
  assembling: 'В сборке',
  assembled: 'Собран',
  awaiting_courier: 'Ожидает курьера',
  in_delivery: 'Доставляется',
  delivered: 'Доставлен',
  ready_for_pickup: 'Готов к выдаче',
  issued: 'Выдан',
  reservation_expired: 'Бронь истекла',
  cancelled: 'Отменён',
}

export type StatusTone = 'info' | 'progress' | 'warning' | 'danger' | 'success' | 'neutral'

/**
 * Тон статуса в ките (`ui-status`): по тому, чей сейчас ход, а не по этапу.
 * Таблица — в docs/admin-design-system.md › Роли.
 */
export const STATUS_TONE: Record<OrderStatus, StatusTone> = {
  needs_attention: 'danger',
  awaiting_payment: 'warning',
  awaiting_courier: 'warning',
  ready_for_pickup: 'warning',
  new: 'info',
  in_delivery: 'info',
  assembling: 'progress',
  assembled: 'progress',
  delivered: 'success',
  issued: 'success',
  payment_expired: 'neutral',
  reservation_expired: 'neutral',
  cancelled: 'neutral',
}

/** Статусы для выпадающего фильтра таблицы, в порядке прохождения заказа. */
export const STATUS_ORDER: OrderStatus[] = [
  'awaiting_payment',
  'payment_expired',
  'new',
  'needs_attention',
  'assembling',
  'assembled',
  'awaiting_courier',
  'in_delivery',
  'ready_for_pickup',
  'delivered',
  'issued',
  'reservation_expired',
  'cancelled',
]

export const statusText = (status: OrderStatus): string => STATUS_TEXT[status] ?? status

/** Статусы курьера от службы доставки; пустой — машину ещё ищут (R-44). */
export const COURIER_STATUS_TEXT: Record<string, string> = {
  '': 'Ищем курьера',
  courier_assigned: 'Курьер едет в аптеку',
  at_pickup: 'Курьер в аптеке',
  picked_up: 'Курьер везёт заказ',
  delivered: 'Доставлено',
  cancelled: 'Служба отменила поездку',
}

/** Незнакомый статус службы — кодом, а не пустотой. */
export const courierStatusText = (status: string): string => COURIER_STATUS_TEXT[status] ?? status

/** Службы доставки, какими их знает сервер (`integration.delivery.provider`). */
export const PROVIDER_TEXT: Record<string, string> = {
  millenium: 'Millenium Taxi',
  yandex: 'Яндекс Доставка',
  stub: 'Тестовая служба',
}

export const providerText = (provider: string): string => PROVIDER_TEXT[provider] ?? provider

export const shipText = (ship: ShipMethod): string =>
  ship === 'delivery' ? 'Доставка' : 'Самовывоз'

/** Заказ закрыт: дальше его двигать некуда. */
export const isDone = (status: OrderStatus): boolean =>
  status === 'delivered' || status === 'issued'

/** Номер заказа, каким его видит клиент. */
export const orderNumber = (order: Pick<Order, 'number'>): string => `№ ${order.number}`

export const ageMinutes = (order: Pick<Order, 'createdAt'>, now: number): number =>
  Math.max(0, Math.round((now - order.createdAt) / 6e4))

/** «41 мин» / «2 ч 5 мин» */
export function ageLabel(minutes: number): string {
  return minutes < 60 ? `${minutes} мин` : `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`
}

/** Срок этапа вышел — карточку надо показать тревожно (BR-20). */
export const isLate = (card: BoardCard): boolean => card.sla?.breached ?? false

/**
 * Сколько минут до конца этапа считается «скоро»: таймер желтеет заранее,
 * чтобы заказ взяли до просрочки, а не после.
 */
export const SOON_MINUTES = 15

export interface SlaView {
  text: string
  state?: 'soon' | 'late'
}

/**
 * Таймер этапа словами: «42 мин осталось» или «12 ч 6 мин · просрочен».
 * Просрочку решает сервер (`breached`) — экран не пересчитывает её по часам.
 */
export function slaView(sla: Sla | null): SlaView | null {
  if (!sla) return null
  const time = ageLabel(Math.abs(sla.minutesLeft))
  if (sla.breached) return { text: `${time} · просрочен`, state: 'late' }
  return {
    text: `${time} осталось`,
    state: sla.minutesLeft <= SOON_MINUTES ? 'soon' : undefined,
  }
}

/** Где забирать или куда везти — одной строкой. */
export function shipWhere(order: Order): string {
  if (order.ship === 'delivery') return order.address || 'Ташкент'
  return order.storeName
}

export const pickedCount = (order: Order): number => order.lines.filter((l) => l.picked).length

/**
 * В какой статус попадёт заказ, если бросить карточку в эту колонку.
 * Колонки «Новые» и «Требует решения» цели не имеют: назад заказ не
 * отматывается, а решение по «зависшему» принимается в его карточке (R-38).
 */
export function columnTarget(columnKey: string): OrderStatus | null {
  switch (columnKey) {
    case 'assembling':
      return 'assembling'
    case 'ready':
      // Сервер сам доведёт до «готов к выдаче» или «ожидает курьера» —
      // это зависит от способа получения, а не от того, куда бросили карточку.
      return 'assembled'
    case 'in_delivery':
      return 'in_delivery'
    default:
      return null
  }
}

/** Можно ли бросить заказ в эту колонку. */
export function canDropTo(card: BoardCard, columnKey: string, canUpdate: boolean): boolean {
  if (!canUpdate) return false
  const target = columnTarget(columnKey)
  if (!target) return false
  if (target === 'assembled') {
    // «К отправке» — это конец сборки: туда попадает только то, что собирают.
    return card.status === 'assembling'
  }
  if (target === 'assembling') return card.status === 'new'
  return card.status === 'awaiting_courier' || card.status === 'assembled'
}

/**
 * Что написать в пустой колонке: не «пусто», а что сюда попадает. Колонки
 * задаёт сервер, поэтому у незнакомой — общая фраза.
 */
const COLUMN_EMPTY: Record<string, string> = {
  new: 'Новых заказов нет. Оформленные заказы появятся здесь сами.',
  assembling: 'Сейчас ничего не собирают. Возьмите заказ из новых.',
  ready: 'Собранных заказов нет. Сюда заказ попадает после сборки.',
  in_delivery: 'В пути заказов нет. Сюда попадает то, что забрал курьер.',
  needs_attention:
    'Проблемных заказов нет. Сюда попадают заказы, которые аптека не может собрать целиком.',
}

export const columnEmpty = (columnKey: string): string =>
  COLUMN_EMPTY[columnKey] ?? 'Здесь пока нет заказов.'

/** Почему карточка не поехала в эту колонку. */
export function dropHint(status: OrderStatus, columnKey: string): string {
  if (!columnTarget(columnKey)) return 'В эту колонку карточки не переносят'
  return `Из «${statusText(status)}» в эту колонку заказ не переводят`
}

export interface Step {
  to: OrderStatus
  label: string
  /** Заказ отдают с оплатой в аптеке: деньги записываются на фармацевта (R-34). */
  cash?: boolean
  /**
   * Короткий глагол для кнопки на карточке доски. Есть только у шага, который
   * единственный и очевидный; где нужен выбор — кнопки нет, есть диалог.
   */
  action?: string
}

/**
 * Что можно сделать с заказом дальше. Выбор собран из текущего статуса и
 * способа получения — тех самых переходов, которые принимает
 * `POST /admin/orders/{id}/status`; порядок проверяет сервер, а экран просто
 * не предлагает невозможного.
 */
export function nextSteps(order: Order): Step[] {
  const delivery = order.ship === 'delivery'
  switch (order.status) {
    case 'new':
      return [{ to: 'assembling', label: 'Взять в сборку', action: 'Взять в сборку' }]
    case 'needs_attention':
      // Зависший заказ сначала решают в его карточке — перенаправить или
      // отказать (R-38); взять в сборку можно, но не одной кнопкой на доске.
      return [{ to: 'assembling', label: 'Взять в сборку' }]
    case 'assembling':
      return [
        delivery
          ? { to: 'assembled', label: 'Сборка закончена — вызвать курьера', action: 'Собран' }
          : {
              to: 'ready_for_pickup',
              label: 'Сборка закончена — готов к выдаче',
              action: 'Собран',
            },
      ]
    case 'assembled':
    case 'awaiting_courier':
      return [{ to: 'in_delivery', label: 'Курьер забрал заказ', action: 'Передать курьеру' }]
    case 'in_delivery':
      return [{ to: 'delivered', label: 'Доставлен клиенту', action: 'Доставлен' }]
    case 'ready_for_pickup':
      return [
        { to: 'issued', label: 'Выдан клиенту' },
        { to: 'issued', label: 'Выдан, деньги получены в аптеке', cash: true },
      ]
    default:
      return []
  }
}

/**
 * Действие этапа для кнопки на карточке: следующий шаг, если он один и не
 * требует выбора. Выдача в аптеке спрашивает про деньги, а зависший заказ —
 * про решение, поэтому у них кнопки нет, остаётся «Сменить статус».
 */
export function stageAction(order: Order): Step | null {
  const step = nextSteps(order)[0]
  return step?.action ? step : null
}

/** Сколько строк на странице можно выбрать; больше 100 сервер не отдаёт. */
export const PAGE_SIZES = [10, 25, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]

export type OrdersTab = 'board' | 'list'

export interface OrderFilters {
  /** Доска смены или список всех заказов. */
  tab: OrdersTab
  q: string
  status: OrderStatus | 'all'
  /** Общий для доски и списка: переключили вкладку — отбор тот же. */
  ship: ShipMethod | 'all'
  /** Страница списка с 1; листает сервер. */
  page: number
  size: PageSize
}

export const EMPTY_FILTERS: OrderFilters = {
  tab: 'board',
  q: '',
  status: 'all',
  ship: 'all',
  page: 1,
  size: 10,
}

const TABS: OrdersTab[] = ['board', 'list']
const SHIPS: OrderFilters['ship'][] = ['all', 'delivery', 'pickup']

function oneOf<T extends string | number>(value: T, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value) ? value : fallback
}

function positive(raw: string | null, fallback: number): number {
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : fallback
}

/**
 * Вкладка и отбор живут в адресе: «Назад» из карточки заказа возвращает на
 * ту же вкладку, страницу и фильтры. Мусор в адресе становится значением по
 * умолчанию, а не ломает экран.
 */
export function filtersFromParams(params: URLSearchParams): OrderFilters {
  return {
    tab: oneOf(params.get('tab') as OrdersTab, TABS, EMPTY_FILTERS.tab),
    q: params.get('q') ?? '',
    status: oneOf(
      params.get('status') as OrderFilters['status'],
      ['all', ...STATUS_ORDER],
      EMPTY_FILTERS.status,
    ),
    ship: oneOf(params.get('ship') as OrderFilters['ship'], SHIPS, EMPTY_FILTERS.ship),
    page: positive(params.get('page'), 1),
    size: oneOf(
      positive(params.get('size'), EMPTY_FILTERS.size) as PageSize,
      PAGE_SIZES,
      EMPTY_FILTERS.size,
    ),
  }
}

/** Обратно в адрес — только то, что отличается от значения по умолчанию. */
export function filtersToParams(filters: OrderFilters): URLSearchParams {
  const params = new URLSearchParams()
  for (const key of Object.keys(EMPTY_FILTERS) as (keyof OrderFilters)[]) {
    if (filters[key] !== EMPTY_FILTERS[key]) params.set(key, String(filters[key]))
  }
  return params
}

/** Сужен ли список: вкладка, страница и её размер — не фильтры. */
export const isFiltered = (filters: OrderFilters): boolean =>
  filters.q !== EMPTY_FILTERS.q ||
  filters.status !== EMPTY_FILTERS.status ||
  filters.ship !== EMPTY_FILTERS.ship

/** Состояние экрана → параметры `GET /admin/orders`: «все» означает «не сужать запрос». */
export function toOrderQuery(filters: OrderFilters): OrderQuery & { ship: ShipMethod | '' } {
  return {
    q: filters.q.trim(),
    status: filters.status === 'all' ? '' : filters.status,
    ship: filters.ship === 'all' ? '' : filters.ship,
    page: filters.page,
    pageSize: filters.size,
  }
}

/** Способ оплаты словами; коды — те, что принимает оформление заказа (R-29). */
const PAY_TEXT: Record<string, string> = {
  pay_at_pharmacy: 'В аптеке',
  card: 'Банковская карта',
  click: 'Click',
  payme: 'Payme',
  uzum: 'Uzum',
  // Приложения, которые приходят только со страницей Rahmat (Multicard, R-110).
  anorbank: 'Anorbank',
  oson: 'Oson',
  alif: 'Alif',
  xazna: 'Xazna',
  beepul: 'Beepul',
  trastpay: 'TrastPay',
  saved_card: 'Сохранённая карта',
}

/** Незнакомый код показываем как есть: лучше код, чем пустое место. */
export const payText = (method: string): string => PAY_TEXT[method] ?? method

/** Оплата в аптеке: при выдаче фармацевт подтверждает, что деньги получены (R-34). */
export const PAY_AT_PHARMACY = 'pay_at_pharmacy'

/**
 * Оплата баллами одной строкой — на доске, в списке и в карточке заказа.
 * `spend_amount` — сколько ушло баллами, `total_to_pay` — что осталось
 * деньгами; к оплате ноль — заказ оплачен баллами целиком и сразу идёт в
 * аптеку. Баллами не платили — пустая строка: строку не показываем.
 */
export function pointsPayText(order: Pick<Order, 'spend' | 'total'>): string {
  if (order.spend <= 0) return ''
  if (order.total <= 0) return 'Полностью баллами'
  return `Частично баллами: ${formatMoney(order.spend)}, к оплате: ${formatMoney(order.total)}`
}

/** Пока товар не ушёл из аптеки, заблокированные на карте деньги можно вернуть (R-105). */
const RELEASABLE: OrderStatus[] = [
  'new',
  'needs_attention',
  'assembling',
  'assembled',
  'ready_for_pickup',
]

/**
 * Можно ли отменить заказ с возвратом денег на карту: деньги заблокированы
 * (`held`), товар ещё в аптеке, и отменяет сотрудник всей сети (`storeId` 0).
 * Сервер проверяет то же самое — здесь только чтобы не показывать лишнюю кнопку.
 */
export function canRelease(
  order: Pick<Order, 'payStatus' | 'status'>,
  storeId: number,
  canUpdate: boolean,
): boolean {
  return (
    canUpdate && storeId === 0 && order.payStatus === 'held' && RELEASABLE.includes(order.status)
  )
}

const PAY_STATUS_TEXT: Record<string, string> = {
  pending: 'ждёт оплаты',
  held: 'заблокирован на карте',
  paid: 'оплачен',
  released: 'возвращён на карту',
  expired: 'время оплаты вышло',
  failed: 'оплата не прошла',
}

/** Состояние платежа; пусто — платежа ещё не было. */
export const payStatusText = (status: string): string =>
  status ? (PAY_STATUS_TEXT[status] ?? status) : 'не оплачивался'

const ACTOR_TEXT: Record<OrderEvent['actor'], string> = {
  customer: 'клиент',
  staff: 'сотрудник',
  system: 'система',
  provider: 'провайдер',
}

/** Кто сделал запись в истории — словом, а не кодом из API. */
export const actorText = (actor: OrderEvent['actor']): string => ACTOR_TEXT[actor] ?? actor

/**
 * Срок брони самовывоза (R-39, F-06). Срок ставит сервер, когда заказ с
 * оплатой в аптеке готов к выдаче; оплаченный заранее заказ ждёт без срока.
 */
export function reservationText(
  order: Pick<Order, 'payMethod' | 'reservationExpires' | 'status'>,
): string {
  if (order.reservationExpires) {
    const until = formatDayTime(order.reservationExpires)
    return order.status === 'reservation_expired' ? `${until} · истекла` : until
  }
  return order.payMethod === PAY_AT_PHARMACY
    ? 'начнётся, когда заказ будет готов к выдаче'
    : 'без срока — заказ оплачен'
}
